import type { ReactNode } from "react";
import { Breadcrumbs, type Crumb } from "@/components/breadcrumbs";
import { cn } from "@/lib/utils";

/**
 * The one boxed surface on the site, for things that genuinely sit on the page — an
 * input group, the answer sheet, a tool. Square-shouldered and flat: a printed panel,
 * not a floating card. Ordinary content sections use <Card>, which is a ruled section
 * rather than a box (see below).
 */
export const CARD_SURFACE = "rounded-md border border-border bg-card";

export const CARD_INTERACTIVE =
  "transition-surface hover:border-foreground/40 hover:bg-secondary/40 focus-visible:border-primary/60";

type CardPadding = "none" | "sm" | "md" | "lg";
const CARD_PADDING: Record<CardPadding, string> = {
  none: "",
  sm: "pt-4",
  md: "pt-5",
  lg: "pt-6",
};

/**
 * Page template: breadcrumbs, an optional kicker, the headline and a standfirst, set
 * like the top of an article. The kicker used to be a tinted pill ("INTERACTIVE MAP"),
 * the most recognisable mark of a generated page; it is plain small type now.
 */
export function PageShell({
  eyebrow,
  title,
  intro,
  crumbs,
  children,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  crumbs?: Crumb[];
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
      <header className="max-w-3xl">
        {crumbs && <Breadcrumbs items={crumbs} />}
        {eyebrow && <p className="label-caps settle text-rust-ink">{eyebrow}</p>}
        <h1
          className={cn(
            "settle font-display text-[2.375rem] font-medium leading-[1.06] tracking-[-0.02em] text-foreground sm:text-[3.25rem]",
            eyebrow && "mt-3",
          )}
        >
          {title}
        </h1>
        {intro && (
          <p className="settle-2 measure mt-5 text-lg leading-relaxed text-pretty text-muted-foreground sm:text-xl sm:leading-relaxed">
            {intro}
          </p>
        )}
      </header>
      <div className="settle-3 mt-10 space-y-12 sm:mt-14">{children}</div>
    </div>
  );
}

/**
 * A content section: a rule across the top and the content beneath it, the way a
 * printed guide separates topics. Was a rounded, shadowed box, and a page of them was
 * the "card soup" look this site no longer wants.
 */
export function Card({
  children,
  className,
  padding = "lg",
}: {
  children: ReactNode;
  className?: string;
  padding?: CardPadding;
}) {
  return (
    <section className={cn("border-t border-rule/70", CARD_PADDING[padding], className)}>
      {children}
    </section>
  );
}

/**
 * A caution set in the margin's voice: a bar in the warning colour down the left and
 * the text beside it. No filled amber box.
 */
export function WarningCard({ children }: { children: ReactNode }) {
  return (
    <div className="border-l-[3px] border-amber py-1 pl-4 text-sm leading-relaxed text-foreground">
      {children}
    </div>
  );
}
